import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Voice dictation via the Web Speech API.
 *
 * On a wall-mounted tablet the keyboard is the worst part of the experience, so talking to
 * it is often the fastest path — "add milk to the shopping list" beats swiping out a word
 * on a screen you're standing in front of.
 *
 * Chrome/Edge only (Firefox has no support); `supported` gates the UI so the mic simply
 * doesn't appear where it wouldn't work.
 */

// The API is still unprefixed-in-name-only, and lib.dom doesn't ship types for it.
interface SpeechRecognitionAlternative {
  transcript: string;
}
interface SpeechRecognitionResult {
  readonly length: number;
  isFinal: boolean;
  [i: number]: SpeechRecognitionAlternative;
}
interface SpeechRecognitionEventLike extends Event {
  resultIndex: number;
  results: { readonly length: number; [i: number]: SpeechRecognitionResult };
}
interface SpeechRecognitionLike extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: Event & { error?: string }) => void) | null;
  onend: (() => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getCtor(): SpeechRecognitionCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function useSpeech(onFinal: (text: string) => void): {
  supported: boolean;
  listening: boolean;
  interim: string;
  toggle: () => void;
} {
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const recognition = useRef<SpeechRecognitionLike | null>(null);
  const supported = useRef(getCtor() !== null).current;

  // Keep the latest callback without re-creating the recognizer on every render.
  const onFinalRef = useRef(onFinal);
  useEffect(() => {
    onFinalRef.current = onFinal;
  }, [onFinal]);

  useEffect(() => {
    const Ctor = getCtor();
    if (!Ctor) return;

    const rec = new Ctor();
    rec.lang = navigator.language || 'en-US';
    rec.continuous = false;
    rec.interimResults = true;

    rec.onresult = (e) => {
      let final = '';
      let pending = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const result = e.results[i];
        if (!result) continue;
        const text = result[0]?.transcript ?? '';
        if (result.isFinal) final += text;
        else pending += text;
      }
      setInterim(pending);
      if (final.trim()) {
        onFinalRef.current(final.trim());
        setInterim('');
      }
    };

    rec.onerror = () => {
      setListening(false);
      setInterim('');
    };
    rec.onend = () => {
      setListening(false);
      setInterim('');
    };

    recognition.current = rec;
    return () => {
      rec.onresult = null;
      rec.onerror = null;
      rec.onend = null;
      rec.abort();
      recognition.current = null;
    };
  }, []);

  const toggle = useCallback(() => {
    const rec = recognition.current;
    if (!rec) return;
    if (listening) {
      rec.stop();
      setListening(false);
      return;
    }
    try {
      rec.start();
      setListening(true);
    } catch {
      // start() throws if it's already running; treat it as a no-op.
    }
  }, [listening]);

  return { supported, listening, interim, toggle };
}
